import {Text} from "@mantine/core";
import {useTranslation} from "react-i18next";
import type {ProffCompanyCard as ProffCompanyCardType} from "../../../types";
import classes from "./CompanyCard.module.css";

type Props = {
    card: ProffCompanyCardType;
};

/** One supporting figure shown in the pale figures panel: a label above a
 *  number, with the number's unit set beside it. A missing value renders
 *  only the label, never an invented number. */
type Figure = {
    key: string;
    label: string;
    number: string | null;
    unit: string | null;
};

/** The business details and supporting figures for a company, shown below
 *  its header (icon, name, org number, type and municipality) inside the
 *  shared preview box. Three parts: industry (Bransje), founding year, and
 *  a figures panel (revenue, profit, employees). Every value is optional:
 *  a null value never renders an invented figure. */
export const CompanyCard = ({card}: Props) => {
    const {t} = useTranslation();

    const revenue = splitDisplay(card.revenueDisplay);
    const profit = splitDisplay(card.profitDisplay);
    const employees =
        card.employees !== null && card.employees !== undefined
            ? String(card.employees)
            : null;

    const figures: Figure[] = [
        {
            key: "revenue",
            label: t("company.revenue"),
            number: revenue?.number ?? null,
            unit: revenue?.unit ?? null,
        },
        {
            key: "profit",
            label: t("company.profit"),
            number: profit?.number ?? null,
            unit: profit?.unit ?? null,
        },
        {
            key: "employees",
            label: card.employeesYear != null
                ? `${t("company.employees")} (${card.employeesYear})`
                : t("company.employees"),
            number: employees,
            unit: null,
        },
    ];

    const industry = splitIndustry(card.industry);
    const hasFoundedYear =
        card.foundedYear !== null && card.foundedYear !== undefined;

    return (
        <div className={classes.card}>
            {industry && (
                <div className={classes.industry}>
                    <Text className={classes.label}>{t("company.industry")}</Text>
                    <Text className={classes.industryValue}>
                        {industry.code ? `${industry.text} (${industry.code})` : industry.text}
                    </Text>
                </div>
            )}

            {hasFoundedYear && (
                <div className={classes.founded}>
                    <Text className={classes.label}>{t("company.founded")}</Text>
                    <Text className={classes.foundedValue}>{card.foundedYear}</Text>
                </div>
            )}

            <div className={classes.figuresPanel}>
                {figures.map((figure) => (
                    <div key={figure.key} className={classes.figure}>
                        <Text className={classes.figureLabel}>{figure.label}</Text>
                        {figure.number && (
                            <div className={classes.figureValueRow}>
                                <Text className={classes.figureValue}>{figure.number}</Text>
                                {figure.unit && (
                                    <Text className={classes.figureUnit}>{figure.unit}</Text>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {card.note && (
                <Text size="xs" c="dimmed" fs="italic" className={classes.note}>
                    {card.note}
                </Text>
            )}
        </div>
    );
};

/** Splits a display string such as "358,8 MNOK" on its last space into a
 *  number and a unit. Returns null for a missing value. */
function splitDisplay(
    display: string | null
): {number: string; unit: string} | null {
    if (!display) return null;
    const splitIndex = display.lastIndexOf(" ");
    if (splitIndex === -1) return {number: display, unit: ""};
    return {
        number: display.slice(0, splitIndex),
        unit: display.slice(splitIndex + 1),
    };
}

/** Groups a 9-digit Norwegian organisation number into threes, e.g.
 *  "934228391" to "934 228 391". Any other shape is left as is. */
export function formatOrgNr(orgNr: string): string {
    if (!/^\d{9}$/.test(orgNr)) return orgNr;
    return orgNr.replace(/(\d{3})(\d{3})(\d{3})/, "$1 $2 $3");
}

/** Splits an industry string that starts with a leading code, such as
 *  "58.290 Utgivelse av annen programvare", into its code and description.
 *  A string with no leading code returns a null code. */
function splitIndustry(
    industry: string | null
): {code: string | null; text: string} | null {
    if (!industry) return null;
    const match = industry.match(/^(\d+(?:\.\d+)*)\s+(.+)$/);
    if (!match) return {code: null, text: industry};
    return {code: match[1], text: match[2]};
}
